import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { API_BASE } from './config';
import { createMonitorSocket } from '../socket/socket';
import '../css/RankTable.css';


const RANKS = [
  'ALL',
  'BG',
  'BGM',
  'NB',
  'NB+',
  'N-',
  'N',
  'N/S',
  'S',
  'S/P'
];
const getAvatarSource = avatarUrl => {
  if (!avatarUrl) return '';

  const value = String(avatarUrl).trim();

  // รูปจาก LINE หรือ URL ภายนอก
  if (
    value.startsWith('http://') ||
    value.startsWith('https://') ||
    value.startsWith('data:') ||
    value.startsWith('blob:')
  ) {
    return value;
  }

  // รูปจาก Backend /uploads
  return `${API_BASE}${value.startsWith('/') ? '' : '/'}${value}`;
};

function RankTable() {

  /* =================================================
     NAVIGATE
  ================================================= */

  const navigate = useNavigate();

  const loggedInUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('user') || 'null');
    } catch {
      return null;
    }
  }, []);

  const profileGroupId = Number(localStorage.getItem('last_group_id')) || 1;

  const openMonitor = () => {
    navigate('/monitor');
  };
  const openMyProfile = () => {
    if (!loggedInUser?.user_id) return;
    navigate(`/player-history/${profileGroupId}/${loggedInUser.user_id}`);
  };
const openPlayerHistory = player => {
  if (!player?.user_id) return;

  navigate(
    `/player-history/${profileGroupId}/${player.user_id}`
  );
};

  /* =================================================
     STATE
  ================================================= */

  const [players, setPlayers] = useState([]);

  const [rankFilter, setRankFilter] =
    useState('ALL');

  const [search, setSearch] =
    useState('');

  const [rankingView, setRankingView] =
    useState('CURRENT');

  const [loading, setLoading] =
    useState(false);

  const [rankInfoOpen, setRankInfoOpen] =
    useState(false);


  /* =================================================
     LOAD
  ================================================= */

  const loadRankTable = useCallback(async () => {

    setLoading(true);

    try {

      const params =
        new URLSearchParams();

      if (rankFilter !== 'ALL') {
        params.set(
          'rank',
          rankFilter
        );
      }

      const query =
        params.toString();

      const url =
        `${API_BASE}/api/game-match/rank-table` +
        `${query ? `?${query}` : ''}`;

      const res =
        await fetch(url);

      const data =
        await res.json();

      if (!res.ok) {
        throw new Error(
          data.message ||
          'Load rank failed'
        );
      }

      setPlayers(
        Array.isArray(data)
          ? data
          : []
      );

    } catch (err) {

      console.error(
        '[LOAD RANK ERROR]',
        err
      );

      setPlayers([]);

    } finally {

      setLoading(false);

    }
  }, [rankFilter]);


  useEffect(() => {
    loadRankTable();
  }, [loadRankTable]);

  useEffect(() => {
    const realtimeSocket = createMonitorSocket();
    const refreshRank = () => loadRankTable();
    const events = [
      'rank-table-updated',
      'match-ended',
      'match-result-updated',
      'point-updated'
    ];

    events.forEach(eventName => realtimeSocket.on(eventName, refreshRank));

    return () => {
      events.forEach(eventName => realtimeSocket.off(eventName, refreshRank));
      realtimeSocket.disconnect();
    };
  }, [loadRankTable]);


  /* =================================================
     SEARCH
  ================================================= */

  const filteredPlayers =
    useMemo(() => {

      const keyword =
        search
          .trim()
          .toLowerCase();

      const searchedPlayers = keyword
        ? players.filter(
        player =>
          String(
            player.name || ''
          )
            .toLowerCase()
            .includes(keyword)
        )
        : [...players];

      if (rankingView === 'TOP_POINTS') {
        return searchedPlayers
          .filter(player => Number(player.finished_match_count || 0) > 0)
          .sort((left, right) =>
            Number(right.accumulated_match_points || 0) -
              Number(left.accumulated_match_points || 0)
          )
          .slice(0, 20);
      }

      if (rankingView === 'TOP_RISERS') {
        return searchedPlayers
          .filter(player => player.movement === 'UP')
          .sort((left, right) =>
            Number(right.movement_count || 0) - Number(left.movement_count || 0)
              || Number(right.latest_change || 0) - Number(left.latest_change || 0)
          )
          .slice(0, 20);
      }

      return searchedPlayers;

    }, [players, rankingView, search]);


  /* =================================================
     MOVEMENT
  ================================================= */

  const renderMovement = player => {

    if (
      player.movement === 'UP'
    ) {
      return (
        <span className="movement-up">
          🔥 ▲ {player.movement_count}
        </span>
      );
    }

    if (
      player.movement === 'DOWN'
    ) {
      return (
        <span className="movement-down">
          ▼ {player.movement_count}
        </span>
      );
    }

    return (
      <span className="movement-same">
        ━
      </span>
    );
  };


  /* =================================================
     POINT CHANGE
  ================================================= */

  const renderPointChange = value => {

    const point =
      Number(value || 0);

    if (point > 0) {
      return (
        <span className="change-plus">
          +{point}
        </span>
      );
    }

    if (point < 0) {
      return (
        <span className="change-minus">
          {point}
        </span>
      );
    }

    return (
      <span className="change-zero">
        0
      </span>
    );
  };


  /* =================================================
     RENDER
  ================================================= */

  return (
    <div className="rank-page">

      {/* ================= HEADER ================= */}

      <div className="rank-header">

        <div>

          <h2>
            🏆 Player Rank ก๊วนน้ามาด
          </h2>

          <div className="rank-subtitle">
            Ranking & Player Points
          </div>

        </div>


        <div className="rank-header-actions">

          <div className="rank-player-count">
            👥 {filteredPlayers.length} คน
          </div>

          <button
            type="button"
            className="rank-monitor-button"
            onClick={openMonitor}
          >
            🏸 Live
          </button>

          {loggedInUser?.user_id && (
            <button
              type="button"
              className="rank-my-profile-button"
              onClick={openMyProfile}
            >
              🔍 {loggedInUser.name || `Member #${loggedInUser.user_id}`}
            </button>
          )}

        </div>

      </div>


      {/* ================= INFO ================= */}

      <div className="rank-info-card">

        <div className="rank-info-header">

          <span className="rank-info-icon">
            ℹ️
          </span>

          <div>

            <div className="rank-info-title">
              ระบบ Ranking
            </div>

            <div className="rank-info-subtitle">
              คะแนนจะเปลี่ยนตามผลการแข่งขัน
            </div>

          </div>

          <button
            type="button"
            className="rank-info-toggle"
            aria-expanded={rankInfoOpen}
            aria-controls="rank-system-details"
            onClick={() => setRankInfoOpen(current => !current)}
          >
            {rankInfoOpen ? 'ซ่อน ▲' : 'แสดง ▼'}
          </button>

        </div>

        {rankInfoOpen && (
          <div id="rank-system-details" className="rank-info-details">
          <div className="rank-rule-list">

          {/* WIN */}

          <div className="rank-rule win">

            <span className="rule-icon">
              🏆
            </span>

            <div>
              <strong>
                ชนะ
              </strong>

              <span>
                คะแนนเพิ่ม
              </span>
            </div>

          </div>


          {/* LOSE */}

          <div className="rank-rule lose">

            <span className="rule-icon">
              ❌
            </span>

            <div>
              <strong>
                แพ้
              </strong>

              <span>
                คะแนนลด
              </span>
            </div>

          </div>


          {/* DRAW */}

          <div className="rank-rule draw">

            <span className="rule-icon">
              🤝
            </span>

            <div>
              <strong>
                เสมอ
              </strong>

              <span>
                คะแนนเท่าเดิม
              </span>
            </div>

          </div>

          </div>


          <div className="rank-auto-message">

          ⚡ เมื่อคะแนนถึงเกณฑ์ของ Rank
          ระบบจะปรับ{' '}

          <strong>
            Rank ขึ้นหรือลงอัตโนมัติ
          </strong>

          </div>
          </div>
        )}

      </div>


      {/* ================= FILTER ================= */}

      <div className="rank-filter-card">

        <div className="rank-filter-title">
          Filter Rank
        </div>


        <div className="rank-buttons">

          {RANKS.map(rank => (

            <button
              key={rank}
              type="button"
              data-rank={rank}
              className={
                rankFilter === rank
                  ? 'rank-filter-btn active'
                  : 'rank-filter-btn'
              }
              onClick={() =>
                setRankFilter(rank)
              }
            >

              {rank === 'ALL'
                ? 'ทั้งหมด'
                : rank}

            </button>

          ))}

        </div>

        <div className="rank-view-buttons" aria-label="รูปแบบการจัดอันดับ">
          <button
            type="button"
            className={`rank-view-btn ${rankingView === 'CURRENT' ? 'active' : ''}`}
            onClick={() => setRankingView('CURRENT')}
          >
            🏆 อันดับปัจจุบัน
          </button>
          <button
            type="button"
            className={`rank-view-btn ${rankingView === 'TOP_POINTS' ? 'active' : ''}`}
            onClick={() => setRankingView('TOP_POINTS')}
          >
            ⭐ คะแนนสะสมสูงสุด Top 20
          </button>
          <button
            type="button"
            className={`rank-view-btn ${rankingView === 'TOP_RISERS' ? 'active' : ''}`}
            onClick={() => setRankingView('TOP_RISERS')}
          >
            🔥 อันดับพุ่งสูงสุด
          </button>
        </div>

        {rankingView === 'TOP_POINTS' && (
          <div className="rank-view-description top-points" role="status">
            ⭐ รวมคะแนนที่ได้รับและเสียจากผลแพ้–ชนะของ Match ที่เล่นจบแล้ว
            ไม่นับ Point ตั้งต้น และแสดงผู้เล่นสูงสุด 20 อันดับ
          </div>
        )}

        {rankingView === 'TOP_RISERS' && (
          <div className="rank-view-description top-risers" role="status">
            🔥 เรียงผู้เล่นที่อันดับขยับขึ้นมากที่สุดหลังผลการแข่งขันล่าสุด
            หากขยับเท่ากันจะเรียงจากคะแนนล่าสุดที่เพิ่มมากกว่า
          </div>
        )}

<div className="rank-extra-filter">

  <input
    type="text"
    className="rank-search"
    placeholder="🔎 ค้นหาชื่อผู้เล่น..."
    value={search}
    onChange={e =>
      setSearch(e.target.value)
    }
  />

  <div className="rank-profile-hint">
    <span className="rank-profile-hint-icon">
      👆
    </span>

    <span>
      คลิกที่
      <strong> รูปหรือชื่อผู้เล่น </strong>
      เพื่อดูโปรไฟล์ ประวัติการแข่งขัน และอัปเดตรูปโปรไฟล์
    </span>
  </div>

</div>

      </div>


      {/* ================= TABLE ================= */}

      <div className="rank-table-card">

        {loading ? (

          <div className="rank-loading">
            กำลังโหลด...
          </div>

        ) : (

          <div className="rank-table-scroll">

            <table className="rank-table">

              <thead>

                <tr>

                  <th>
                    อันดับ
                  </th>

                  <th>
                    ผู้เล่น
                  </th>

                  <th>
                    Rank
                  </th>

                  <th>
                    {rankingView === 'TOP_POINTS' ? 'คะแนนสะสม' : 'Point'}
                  </th>

                  <th>
                    ล่าสุด
                  </th>

                  <th>
                    อันดับ
                  </th>

                </tr>

              </thead>


              <tbody>

                {filteredPlayers.map(
                  player => (

                    <tr
                      key={
                        player.user_id
                      }
                    >

                      {/* POSITION */}

                      <td className={`rank-number ${
                        player.movement === 'UP'
                          ? 'rank-number-up'
                          : player.movement === 'DOWN'
                            ? 'rank-number-down'
                            : 'rank-number-same'
                      }`}>

                        {player.position === 1
                          ? '🥇'
                          : player.position === 2
                          ? '🥈'
                          : player.position === 3
                          ? '🥉'
                          : `#${player.position}`}

                      </td>


                      {/* PLAYER */}

                    <td className="rank-player-name">

  <button
    type="button"
    className="rank-player-link"
    onClick={() =>
      openPlayerHistory(player)
    }
    title={`ดูประวัติของ ${player.name}`}
  >

    <span className="rank-player-avatar">

      {(player.avatarUrl ||
        player.avatar_url ||
        player.avatar) ? (

        <img
          src={getAvatarSource(
            player.avatarUrl ||
            player.avatar_url ||
            player.avatar
          )}
          alt={player.name || 'ผู้เล่น'}
          onError={event => {
            event.currentTarget.style.display =
              'none';

            const fallback =
              event.currentTarget.nextElementSibling;

            if (fallback) {
              fallback.style.display = 'grid';
            }
          }}
        />

      ) : null}

      <span
        className="rank-player-avatar-fallback"
        style={{
          display:
            (
              player.avatarUrl ||
              player.avatar_url ||
              player.avatar
            )
              ? 'none'
              : 'grid'
        }}
      >
        {player.name
          ?.trim()
          ?.charAt(0)
          ?.toUpperCase() || '🏸'}
      </span>

    </span>

    <span className="rank-player-name-text">
      {player.name}
    </span>

  </button>

</td>


                      {/* RANK */}

                      <td>

                        <span className="rank-badge">
                          {player.rank || '-'}
                        </span>

                      </td>


                      {/* POINT */}

                      <td>

                        <span className="rank-point">
                          {rankingView === 'TOP_POINTS'
                            ? Number(player.accumulated_match_points || 0)
                            : player.point ?? 0}
                        </span>

                      </td>


                      {/* POINT CHANGE */}

                      <td>

                        {renderPointChange(
                          player.latest_change
                        )}

                      </td>


                      {/* MOVEMENT */}

                      <td>

                        {renderMovement(
                          player
                        )}

                      </td>

                    </tr>

                  )
                )}


                {filteredPlayers.length === 0 && (

                  <tr>

                    <td
                      colSpan="6"
                      className="rank-empty"
                    >
                      ไม่พบผู้เล่น
                    </td>

                  </tr>

                )}

              </tbody>

            </table>

          </div>

        )}

      </div>

    </div>
  );
}


export default RankTable;
